// First-login onboarding: a welcome modal where the user picks a role
// (创作者 / 需求方), followed by a role-aware spotlight tour (driver.js).
// Anchored steps spotlight a header element; narrated steps (no anchor)
// render as centered cards for stages that live deep inside a flow.
//
// Trigger: mounted in the global Layout. On first login (profile loads
// with onboardingCompleted !== true) the role chooser shows once. On
// finish/skip it calls the complete endpoint so it won't auto-show again.
//
// Replay: the header's 功能引导 button dispatches `open-onboarding-chooser`
// to re-open the role chooser; the settings entry dispatches
// `replay-onboarding` to replay directly using the profile role.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Palette, ClipboardList } from 'lucide-react';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './onboarding.css';
import type { UserProfile } from '../../types/api';
import { userService } from '../../services/userService';
import { tourForRole, type TourStep } from './tourSteps';

type GuideRole = 'creator' | 'client';

// Poll for an element up to ~2s (pages render async after navigation).
function waitForAnchor(selector: string, timeoutMs = 2000): Promise<Element | null> {
  return new Promise((resolve) => {
    const existing = document.querySelector(selector);
    if (existing) return resolve(existing);
    const started = performance.now();
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) return resolve(el);
      if (performance.now() - started > timeoutMs) return resolve(null);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [showChooser, setShowChooser] = useState(false);
  // When true, the chooser was opened manually (功能引导) — don't gate on
  // onboardingCompleted and don't need a loaded profile.
  const manualRef = useRef(false);
  const startedRef = useRef(false);
  // Active driver instance, so a new run can tear down a stale one.
  const driverRef = useRef<Driver | null>(null);

  // Load the current user's profile when logged in (and on auth change).
  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!localStorage.getItem('token')) { setProfile(null); startedRef.current = false; return; }
      try {
        const res = await userService.getUserProfile({ suppressAuthPrompt: true });
        if (active && res.success && res.data?.userProfile) setProfile(res.data.userProfile);
      } catch { /* ignore */ }
    };
    void load();
    window.addEventListener('auth-change', load);
    return () => { active = false; window.removeEventListener('auth-change', load); };
  }, []);

  const runTour = useCallback((role: GuideRole) => {
    const steps = tourForRole(role);

    // Tear down any prior run.
    driverRef.current?.destroy();

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      void userService.completeOnboarding().catch(() => { /* best-effort */ });
    };

    // We drive manually (rather than handing all steps to driver.js up
    // front) because most anchors only exist after we navigate to their
    // page. Each move: navigate if needed → wait for the anchor → render
    // a single-step popover with our own nav buttons.
    const d = driver({
      showProgress: true,
      progressText: '{{current}} / {{total}}',
      allowClose: true,
      onDestroyed: () => finish(),
    });
    driverRef.current = d;

    const total = steps.length;

    const show = async (i: number) => {
      if (i < 0 || i >= total) { d.destroy(); return; }
      const step: TourStep = steps[i];

      // Navigate to the step's page if it declares one and we're not there.
      if (step.route) {
        const [path] = step.route.split('?');
        if (window.location.pathname !== path || step.route.includes('?')) {
          navigate(step.route);
        }
      }

      const selector = typeof step.element === 'string' ? step.element : undefined;
      const anchor = selector ? await waitForAnchor(selector) : null;

      if (finished) return; // closed while we were waiting

      const isFirst = i === 0;
      const isLast = i === total - 1;
      const popover = {
        ...(step.popover || {}),
        progressText: `${i + 1} / ${total}`,
        showButtons: ['next', 'previous', 'close'] as ('next' | 'previous' | 'close')[],
        nextBtnText: isLast ? '完成' : '下一步',
        prevBtnText: '上一步',
        // Drive navigation ourselves so we can route between pages.
        onNextClick: () => { if (isLast) d.destroy(); else void show(i + 1); },
        onPrevClick: () => { if (!isFirst) void show(i - 1); },
      };

      // Anchored step with a live target → spotlight it; otherwise (narrated
      // step, or anchor missing) → centered card so we never stall.
      if (anchor && selector) {
        d.highlight({ element: selector, popover });
      } else {
        d.highlight({ popover: { ...popover, align: 'center', side: 'over' } });
      }
    };

    void show(0);
  }, [navigate]);

  // First-login: show the chooser once when the profile arrives uncompleted.
  useEffect(() => {
    if (!profile || startedRef.current) return;
    if (profile.onboardingCompleted) return;
    startedRef.current = true;
    manualRef.current = false;
    setShowChooser(true);
  }, [profile]);

  // 功能引导 button — re-open the role chooser on demand.
  useEffect(() => {
    const open = () => { manualRef.current = true; setShowChooser(true); };
    window.addEventListener('open-onboarding-chooser', open);
    return () => window.removeEventListener('open-onboarding-chooser', open);
  }, []);

  // Settings "重新查看新手引导" — replay directly with the profile role.
  useEffect(() => {
    const replay = () => {
      setTimeout(() => runTour(profile?.role === 'client' ? 'client' : 'creator'), 50);
    };
    window.addEventListener('replay-onboarding', replay);
    return () => window.removeEventListener('replay-onboarding', replay);
  }, [profile, runTour]);

  const pickRole = (role: GuideRole) => {
    setShowChooser(false);
    // Let the modal unmount before the spotlight measures targets.
    setTimeout(() => runTour(role), 200);
  };

  const handleSkip = () => {
    setShowChooser(false);
    // Only persist completion when skipping the first-login prompt; a
    // manual replay shouldn't flip the flag (it's already completed).
    if (!manualRef.current) {
      void userService.completeOnboarding().catch(() => { /* best-effort */ });
    }
  };

  if (!showChooser) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-8 text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          欢迎来到 CraftCloud 云作
        </h2>
        <p className="text-sm text-gray-500 leading-relaxed mb-6">
          选择你的身份，我们用 1 分钟带你了解关键功能与站内交易流程。
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <button
            onClick={() => pickRole('creator')}
            className="group rounded-2xl border border-gray-200 p-5 text-left transition-all hover:border-black hover:shadow-md"
          >
            <Palette className="w-6 h-6 text-gray-700 mb-2" />
            <div className="text-base font-semibold text-gray-900 mb-1">我是创作者</div>
            <div className="text-xs text-gray-500 leading-relaxed">
              发布作品与服务、接需求、签约交付、安全收款。
            </div>
          </button>
          <button
            onClick={() => pickRole('client')}
            className="group rounded-2xl border border-gray-200 p-5 text-left transition-all hover:border-black hover:shadow-md"
          >
            <ClipboardList className="w-6 h-6 text-gray-700 mb-2" />
            <div className="text-base font-semibold text-gray-900 mb-1">我是需求方</div>
            <div className="text-xs text-gray-500 leading-relaxed">
              发布需求、找创作者、签约、按里程碑打款与验收。
            </div>
          </button>
        </div>

        <button
          onClick={handleSkip}
          className="w-full py-2.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
        >
          跳过
        </button>
      </div>
    </div>
  );
};

export default Onboarding;
